import React, { useState, useCallback, useEffect, useRef } from 'react';

import { ClickAwayListener, MenuItem, TableRow, Popover } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { StyledTableCell, StyledTextField } from 'components/ObjectiveTagList/styles';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { CheckRadio } from 'screens/home/tasksList/styles';
import styled, { css } from 'styled-components';
import { NewStyledIconButton, PaddingCSS, StyledBox } from 'styles/common';

import { getColorFromKey, haveSamePropValues } from 'constants/helper';
import { CaretDownIcon, DotsIcon, StyledIcon, UncheckedCircle } from 'constants/icons';
import theme from 'constants/theme';

import { showErrorSnackbar } from 'services/snackbar';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';

import ExpandedFunctionInCard from './ExpandedFunctionInCard';
import GradeAndRoleRow from './GradeAndRoleRow';

// helpers for this component only.
// keeping them here makes more sense than moving them up in helpers.
const MAX_EXPANSION_LEVEL = 4;
const VALIDATION_ERROR_TIMEOUT = 2500;

export const paddingFactor = 24;
export const iconSize = 24;
export const horizontalLineWidth = paddingFactor + iconSize / 2;

export const getLeftPadding = (nestingLevel) => {
  switch (nestingLevel) {
    case 0:
      return `${paddingFactor}px`;
    case 1:
      return `${paddingFactor + paddingFactor + iconSize}px`;
    case 2:
      return `${3 * paddingFactor + paddingFactor + iconSize}px`;
    case 3:
      return `${5 * paddingFactor + paddingFactor + iconSize}px`;
    case 4:
      return `${7 * paddingFactor + paddingFactor + iconSize}px`;
    default:
      return `${paddingFactor}px`;
  }
};

export const getLeftPosition = (nestingLevel) => {
  switch (nestingLevel) {
    case 0:
      return `${paddingFactor}px`;
    case 1:
      return `${paddingFactor + iconSize / 2}px`;
    case 2:
      return `${2 * paddingFactor + paddingFactor + iconSize / 2}px`;
    case 3:
      return `${4 * paddingFactor + paddingFactor + iconSize / 2}px`;
    case 4:
      return `${6 * paddingFactor + paddingFactor + iconSize / 2}px`;
    default:
      return `${paddingFactor}px`;
  }
};

export const StyledVerticalLine = styled.span`
  position: absolute;
  top: -15px;
  bottom: ${(props) => (props.bottom ? props.bottom : '-1px')};
  left: ${(props) => (props.left ? props.left : 0)};
  display: inline-block;
  width: 1px;
  background-color: ${getColorFromKey('primary')};
`;

export const StyledHorizontalLine = styled.span`
  position: absolute;
  left: ${(props) => `-${horizontalLineWidth}px`};
  top: 50%;
  display: inline-block;
  height: 1px;
  width: ${(props) => props.width};
  background-color: ${getColorFromKey('primary')};
`;

const getVerticalLines = (nestingLevel, isCreateNewRow = false, levelsForWhichLinesToBeDrawn = []) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {levelsForWhichLinesToBeDrawn.map((num) => (
        <StyledVerticalLine key={`${nestingLevel}+${num}`} left={`-${getLeftPosition(nestingLevel + 1 - num)}`} />
      ))}
      <StyledHorizontalLine width={`${horizontalLineWidth}px`} />
      <StyledVerticalLine left={`-${horizontalLineWidth}px`} bottom={isCreateNewRow ? '50%' : '-1px'} />
    </>
  );
};

const showExpansionIcon = (nestingLevel) => nestingLevel < MAX_EXPANSION_LEVEL;

export const ExpansionControl = ({
  nestingLevel,
  handleExpansionClick, // pass functions using callback hook to save renders.
  // handleAddChild,
  hasChildTags,
  // canHaveChildTags,
  isCreateNewRow,
  isExpanded,
  levelsForWhichLinesToBeDrawn,
}) => (
  // return useMemo( () =>
  <>
    {getVerticalLines(nestingLevel, isCreateNewRow, levelsForWhichLinesToBeDrawn)}
    <StyledExpansionContainer>
      {showExpansionIcon(nestingLevel) && (
        <>
          {hasChildTags && (
            <NewStyledIconButton
              rightGutter={1}
              padding="none"
              height="max-content"
              onClick={handleExpansionClick}
              color="primary"
            >
              <PlayIconContainer>
                <PlayIcon rotate={isExpanded} />
              </PlayIconContainer>
            </NewStyledIconButton>
          )}
          {/* {
                canHaveChildTags && 
                  <NewStyledIconButton height='max-content' onClick={handleAddChild} color="textSecondary">
                    <StyledIcon size="header">
                      <AddCircleOutlineRoundedIcon />
                    </StyledIcon>
                  </NewStyledIconButton>
              } */}
        </>
      )}
    </StyledExpansionContainer>
  </>
);

// ,[nestingLevel, handleExpansionClick ]);

const HashTagComp = ({
  name,
  handleKeyDown,
  handleTagChange,
  validationError,
  isCreateNewRow,
  children,
  nestingLevel,
  isEditing,
  isExpandable,
  subTypeIcon = null,
  modalView,
  getFocus,
  idx,
  showExpandedCard,
}) => (
  // return useMemo( () =>
  <StyledTableCell
    paddingtop="0"
    paddingBottom="0"
    paddingLeft={getLeftPadding(nestingLevel)}
    display="flex"
    border={!showExpandedCard}
  >
    <FlexContainer paddingLeft="0">
      {children}
      <TextFieldContainer isEditing={isEditing}>
        {!isEditing && subTypeIcon}
        <StyledTextField
          disabled={!isEditing}
          name="name"
          placeholder="Name"
          fullWidth
          variant="outlined"
          value={name}
          onKeyDown={handleKeyDown}
          onChange={handleTagChange}
          autoFocus={getFocus}
        />
      </TextFieldContainer>
      {validationError && <ErrorSubText center>Special characters and leading spaces are not allowed</ErrorSubText>}
    </FlexContainer>
  </StyledTableCell>
);
// ,[validationError, handleKeyDown, handleTagChange, name])
const FunctionAndGradeEditableRow = ({
  func = {},
  onUpdate,
  nestingLevel = 0,
  isExpandable = true,
  isEditing,
  toggleEditing,
  gradesForOrg,
  // being not active is considered as deleted.
  onActiveToggle,
  hasChildTags = true,
  canHaveChildTags = true,
  enableExpansion: enableParentExpansion = () => {},
  disableExpansion: disableParentExpansion = () => {},

  modalView = false,
  getFocus = false,
  enableCreation = true,
  selectTag,
  levelsForWhichLinesToBeDrawn = [],
  isLastRow,
  editingTags = [],
  setEditingTags = () => {},
  refetchData,
  idx,
  parentArrayOfFunction = [],
  jobFunctionId,
  jobGradeUI,
  jobRoleId,
  onSelect = () => {},
}) => {
  /* states and derived states */
  const isCreateNewRow = !canHaveChildTags;
  const [currentFunc, setCurrentFunc] = useState(func);
  const [validationError, setValidationError] = useState(false);

  const [showExpandedCard, setShowExpandedCard] = useState(false);
  const [showChildTagCreationRow, setShowChildTagCreationRow] = useState(false);

  const dummyRef = useRef(null);

  const dispatch = useDispatch();

  const { grades } = func;

  const { name, uuid: currentFuncUuid } = currentFunc.function;

  const checked = currentFuncUuid === String(jobFunctionId);

  useEffect(() => {
    if ((parentArrayOfFunction.includes(currentFuncUuid) || checked) && modalView) {
      setShowExpandedCard(true);
      // if (funcRef?.current) {
      //   funcRef.current.scrollIntoView({
      //     behavior: 'smooth',
      //     block: 'end',
      //     inline: 'start'
      //   });
      // }
    }
  }, []);

  // const { name, functionUuid: uuid } = value;

  /* functions and handlers */
  const handleExpansionClick = (event) => {
    event.stopPropagation();
    setShowExpandedCard((prev) => !prev);
  };

  const handleAddClick = (event) => {
    event.stopPropagation();
    setShowChildTagCreationRow(true);
  };

  const updateTag = (func) => {
    const trimmedName = String(func.name).trimEnd();
    onUpdate({ ...func, name: trimmedName });
  };

  const handleOnChange = useCallback(({ target: { name, value } }) => {
    setCurrentFunc((oldTag) => ({ ...oldTag, [name]: value }));
  }, []);

  const handleToggleActive = useCallback(
    (event) => {
      onActiveToggle(event, func);
    },
    [func]
  );

  const handleTagChange = useCallback(
    ({ target }) => {
      const regex = /^\w[\w\s]*$/;
      setValidationError(false);
      if (target.value === '' || regex.test(target.value)) {
        handleOnChange({ target });
      } else {
        setValidationError(true);
        setTimeout(() => setValidationError(false), VALIDATION_ERROR_TIMEOUT);
      }
    },
    [handleOnChange]
  );

  const handleKeyDown = ({ keyCode, key, target }) => {
    if (keyCode === 27) {
      // this will work since everytime update is passed up to parent.
      target.blur();
      cancelEditing();
    }
    if (key === 'Enter') {
      updateTag(currentFunc);
    }
  };

  const cancelEditing = () => {
    setCurrentFunc(func);
    if (isEditing) toggleEditing(func.id);
  };

  // listen to changes in parent tags array.
  useEffect(() => {
    setCurrentFunc(func);
  }, [func.active, func.name, func.jobGradeList, func.id, isEditing]);

  const enableEditing = useCallback(() => {
    if (!isEditing) toggleEditing(func.id);
  }, [func.id, isEditing]);

  const handleClickAway = useCallback(() => {
    if (isEditing) {
      const propertyList = ['name', 'displayName', 'active', 'description', 'tagSubType'];
      if (haveSamePropValues(func, currentFunc, propertyList)) cancelEditing();
      else updateTag(currentFunc);
    }
  }, [currentFunc, isEditing, updateTag]);

  const handleSaveTag = useCallback(() => {
    const tagRowPropertyList = ['name', 'displayName', 'active', 'description', 'tagSubType'];
    if (isEditing) {
      if (haveSamePropValues(func, currentFunc, tagRowPropertyList)) cancelEditing();
      else if (name) {
        updateTag(currentFunc);
      } else {
        dispatch(showErrorSnackbar('Please fill required fields!'));
      }
    }
  }, [currentFunc, isEditing, updateTag]);

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <StyledTableRow isEditing={isEditing}>
          <HashTagComp
            isEditing={isEditing}
            name={name}
            handleTagChange={handleTagChange}
            handleKeyDown={handleKeyDown}
            validationError={validationError}
            nestingLevel={nestingLevel}
            isCreateNewRow={isCreateNewRow}
            isExpandable={isExpandable}
            subTypeIcon={null}
            modalView={modalView}
            getFocus={getFocus}
            idx={idx}
            showExpandedCard={showExpandedCard}
          >
            {isExpandable && (
              <ExpansionControl
                nestingLevel={nestingLevel}
                handleExpansionClick={handleExpansionClick}
                handleAddChild={handleAddClick}
                hasChildTags={hasChildTags}
                isCreateNewRow={enableCreation ? isCreateNewRow : isLastRow}
                canHaveChildTags={false}
                isExpanded={showExpandedCard}
                levelsForWhichLinesToBeDrawn={[...levelsForWhichLinesToBeDrawn, nestingLevel - 1]}
              />
            )}
          </HashTagComp>
          {modalView && (
            <StyledTableCell textAlign="right" paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
              <CheckRadio
                disableRipple
                checked={checked}
                style={{ marginLeft: 'auto', padding: 0 }}
                checkedIcon={<UncheckedCircle size="smallest" color={theme.palette.primary.main} />}
                icon={<UncheckedCircle size="smallest" color="textSecondary" />}
                hoverColor="textSecondary"
                onClick={() => {
                  onSelect({
                    jobFunctionId: +currentFuncUuid,
                  });
                }}
              />
            </StyledTableCell>
          )}
        </StyledTableRow>
      </ClickAwayListener>
      {(showExpandedCard || showChildTagCreationRow) && (
        <GradeAndRoleRow
          parentTag={func}
          nestingLevel={nestingLevel + 1}
          grades={grades}
          showExpandedCard={showExpandedCard}
          enableParentExpansion={enableParentExpansion}
          disableParentExpansion={disableParentExpansion}
          modalView={modalView}
          enableCreation={enableCreation}
          selectTag={selectTag}
          levelsForWhichLinesToBeDrawn={[...levelsForWhichLinesToBeDrawn, nestingLevel + 1]}
          editingTags={editingTags}
          setEditingTags={setEditingTags}
          gradesForOrg={gradesForOrg}
          refetchData={refetchData}
          parentArrayOfFunction={parentArrayOfFunction}
          jobFunctionId={jobFunctionId}
          jobGradeUI={jobGradeUI}
          jobRoleId={jobRoleId}
          onSelect={onSelect}
        />
      )}
      {(showExpandedCard || showChildTagCreationRow) && (
        <ExpandedFunctionInCard
          showExpandedCard={showExpandedCard}
          func={func}
          nestingLevel={nestingLevel + 1}
          levelsForWhichLinesToBeDrawn={[...levelsForWhichLinesToBeDrawn, nestingLevel + 1]}
          parentArrayOfFunction={parentArrayOfFunction}
          jobFunctionId={jobFunctionId}
          jobGradeUI={jobGradeUI}
          jobRoleId={jobRoleId}
          modalView={modalView}
          onSelect={onSelect}
        />
      )}
    </>
  );
};

const OptionsComponent = ({ disableTag }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <FullWidthBox flexcontainer justifycontent="flex-end" alignitems="center">
        <StyledCircularOutlineIcon onClick={handleClick} color="primary" icon={StyledDotsIcon} />
      </FullWidthBox>
      <Popover
        id="objective-func-actions"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={disableTag}>
          <StyledIcon size="header" color="primary" leftGutter={1} rightGutter={2}>
            <DeleteIcon />
          </StyledIcon>
          Delete
        </MenuItem>
      </Popover>
    </>
  );
};

const ErrorSubText = styled.span`
  font-size: 12px;
  margin: -5px 0 0 0;
  display: inline-block;
  color: red;
  opacity: 0.75;
  line-height: 1rem;
`;

const StyledExpansionIcon = styled(StyledIcon)`
  transform: rotate(-90deg);
`;

const StyledExpansionContainer = styled.div`
  display: flex;
  align-items: center;
  ${PaddingCSS}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  ${PaddingCSS};
`;

const StyledCircularOutlineIcon = styled(CircularOutlineIcon)`
  visibility: hidden;
`;

const StyledTableRow = styled(TableRow)`
  ${(props) =>
    props.modalView &&
    !props.isEditing &&
    css`
      td * {
        cursor: pointer;
      }
    `};

  ${(props) =>
    props.isEditing &&
    css`
      background-color: #e9f6ff;
    `}

  &:hover {
    background-color: #e9f6ff;
    ${StyledCircularOutlineIcon} {
      visibility: visible;
    }
  }
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 40px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 0px;


  
  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border: 1px solid ${props.theme.palette.borders.lighterGrey};
      border-radius: 5px;
    `}
    input {
      padding-left 12px;
    }
`;

const SpaceAroundContainer = styled.div`
  display: flex;
  justify-content: space-around;
`;

export const PlayIconContainer = styled.div`
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px !important;
  width: 24px !important;
  border: ${(props) => `1px solid ${theme.palette.primary.main}`};
  border-radius: 50%;
  border-color: ${(props) => theme.palette.primary.main};
`;

export const PlayIcon = styled(CaretDownIcon)`
  svg {
    width: 10px;
    height: 10px;
    margin-top: 2px;
    margin-left: 2px;
    fill: ${(props) => props.theme.palette.primary.main};
    ${({ rotate }) =>
      rotate &&
      `transform: rotate(90deg);
        margin-left: -2px;
        margin-top: 3px;
    `};
  }
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

const StyledSelectDropdown = styled(Select)`
  height: 40px;
  ${(props) =>
    !props.isEditing &&
    css`
      .driver-select__control {
        border-color: transparent;
        background-color: transparent;
      }
      .driver-select__indicator-separator {
        background-color: transparent;
      }
      .driver-select__dropdown-indicator {
        visibility: hidden;
      }
      .driver-select__control:hover .driver-select__dropdown-indicator {
        visibility: visible;
      }
    `}
  .driver-select__single-value {
    font-size: 14px;
  }
  .driver-select__placeholder {
    font-size: 14px;
  }
`;

const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;

export default FunctionAndGradeEditableRow;
